/**
 * The Bexio contact → tenancy mapping modal.
 *
 * MOVED VERBATIM out of `AdminBexioPage.js` (UI-4b) so the legacy page, the new
 * contacts page and the contact drawer all drive ONE implementation. Two copies
 * is how a fail-closed rule ends up enforced in one place and not the other.
 *
 * Nothing about its behaviour changed in the move: no preselection (`selectedId`
 * starts empty, and a multi-candidate contact is explicitly told to choose rather
 * than being given a default), the suggestion fetch, the confirm / change / remove
 * calls, and the server's own error text on failure are all as they were. The
 * existing `AdminBexioPage` characterization tests cover it through the page and
 * had to stay green across this move.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import Badge from "./BexioBadge";
import {
  changeBexioContactMapping,
  confirmBexioContactMapping,
  deleteBexioContactMapping,
  getBexioContactMappingSuggestions,
} from "../../../../api/adminData/bexio";

/* Moved with this modal: the page no longer references either. */

const CONFIDENCE_LABEL = {
  exact: "Exakt",
  high: "Hoch",
  medium: "Mittel",
  low: "Niedrig",
  conflict: "Konflikt",
};

function confidenceTone(c) {
  if (c === "exact" || c === "high") return "good";
  if (c === "medium") return "warn";
  if (c === "conflict") return "bad";
  return "neutral";
}

// ─── B1c-v1: Contact → Tenancy mapping modal ────────────────────────

export default function BexioContactMappingModal({ contact, currentStatus, onClose, onChanged }) {
  const [suggestions, setSuggestions] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [loadingSug, setLoadingSug] = useState(true);
  const [selectedId, setSelectedId] = useState("");
  const [busy, setBusy] = useState(false);

  const isMapped = currentStatus?.mapping_status === "mapped";
  const isInvalid = currentStatus?.mapping_status === "invalid";
  const isExisting = isMapped || isInvalid; // an existing mapping row to manage
  const isConflictLike =
    currentStatus?.mapping_status === "conflict" ||
    currentStatus?.mapping_status === "not_contact_level_mappable";

  useEffect(() => {
    let alive = true;
    setLoadingSug(true);
    getBexioContactMappingSuggestions(contact.bexio_contact_id)
      .then((res) => {
        if (!alive) return;
        const sug = Array.isArray(res?.suggestions) ? res.suggestions : [];
        setSuggestions(sug);
        setWarnings(Array.isArray(res?.warnings) ? res.warnings : []);
        // Preselect ONLY for a NEW mapping (never when a mapping already exists —
        // Fix 1: an existing/invalid mapping must never one-click retarget) and
        // only a single exact/high candidate with no blocking warning.
        if (
          !isExisting &&
          sug.length === 1 &&
          (sug[0].confidence === "exact" || sug[0].confidence === "high") &&
          !sug[0].is_multi_tenancy_contact_warning &&
          (sug[0].blocking_warnings || []).length === 0
        ) {
          setSelectedId(sug[0].matched_entity_id);
        }
      })
      .catch((e) => {
        if (alive)
          toast.error(e?.message || "Vorschläge konnten nicht geladen werden.");
      })
      .finally(() => {
        if (alive) setLoadingSug(false);
      });
    return () => {
      alive = false;
    };
  }, [contact.bexio_contact_id, isExisting]);

  const handleConfirm = async () => {
    if (!selectedId || busy) return;
    setBusy(true);
    try {
      if (isMapped) {
        // Deliberate retarget of an existing mapping → PATCH.
        await changeBexioContactMapping(contact.bexio_contact_id, {
          vantio_entity_id: selectedId,
        });
        toast.success("Zuordnung geändert.");
      } else {
        // Create-only confirm → POST.
        await confirmBexioContactMapping(contact.bexio_contact_id, {
          vantio_entity_id: selectedId,
        });
        toast.success("Zuordnung bestätigt.");
      }
      await onChanged();
    } catch (e) {
      toast.error(e?.message || "Zuordnung fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await deleteBexioContactMapping(contact.bexio_contact_id);
      toast.success("Zuordnung entfernt.");
      await onChanged();
    } catch (e) {
      toast.error(e?.message || "Entfernen fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="max-h-[85vh] w-full max-w-[900px] overflow-y-auto rounded-[12px] border border-border bg-card p-5">
        <div className="mb-4 flex items-start justify-between">
          <h2 className="text-[16px] font-bold text-foreground">
            Bexio-Kontakt zuordnen
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Schließen"
          >
            ✕
          </button>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {/* Left: Bexio contact */}
          <div className="rounded-[10px] border border-border bg-card p-4">
            <div className="text-[10px] uppercase tracking-[0.09em] text-muted-foreground">
              Bexio-Kontakt
            </div>
            <div className="mt-2 text-[14px] font-semibold text-foreground">
              {contact.display_name || "—"}
            </div>
            <div className="mt-1 text-[12px] text-foreground">
              {contact.mail || "—"}
            </div>
            <div className="mt-1 text-[11px] text-muted-foreground">
              Kontakt-Nr.: {contact.bexio_contact_id}
            </div>
            <div className="text-[11px] text-muted-foreground">
              Typ: {contact.contact_type || "—"}
            </div>

            {isMapped ? (
              <div className="mt-3 rounded-[8px] border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 p-3">
                <div className="text-[10px] uppercase tracking-[0.08em] text-emerald-700 dark:text-emerald-400">
                  Aktuelle Zuordnung
                </div>
                <div className="mt-1 text-[13px] text-foreground">
                  {currentStatus.mapped_tenancy_reference ||
                    currentStatus.mapped_entity_id}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {(currentStatus.mapped_tenant_names || []).join(", ")}
                  {currentStatus.mapped_room_name
                    ? ` · ${currentStatus.mapped_room_name}`
                    : ""}
                  {currentStatus.mapped_unit_label
                    ? ` · ${currentStatus.mapped_unit_label}`
                    : ""}
                </div>
                <button
                  type="button"
                  onClick={handleRemove}
                  disabled={busy}
                  className="mt-2 rounded-[8px] border border-red-200 dark:border-red-500/30 px-3 py-1 text-[12px] text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-500/20 disabled:opacity-60"
                >
                  Zuordnung entfernen
                </button>
              </div>
            ) : null}

            {isInvalid ? (
              <div className="mt-3 rounded-[8px] border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-3">
                <div className="text-[10px] uppercase tracking-[0.08em] text-red-700 dark:text-red-300">
                  Ungültige Zuordnung
                </div>
                <div className="mt-1 text-[13px] text-foreground">
                  {currentStatus?.mapped_tenancy_reference ||
                    currentStatus?.mapped_entity_id ||
                    "—"}
                  {currentStatus?.mapped_entity_type
                    ? ` (${currentStatus.mapped_entity_type})`
                    : ""}
                </div>
                <div className="mt-1 text-[11px] text-red-700 dark:text-red-300">
                  Ziel-Mietverhältnis existiert nicht mehr oder ist nicht sichtbar.
                </div>
                <button
                  type="button"
                  onClick={handleRemove}
                  disabled={busy}
                  className="mt-2 rounded-[8px] border border-red-200 dark:border-red-500/30 px-3 py-1 text-[12px] text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-500/20 disabled:opacity-60"
                >
                  Zuordnung entfernen
                </button>
              </div>
            ) : null}
          </div>

          {/* Right: suggestions */}
          <div>
            {warnings.length > 0 || isConflictLike ? (
              <div className="mb-3 rounded-[8px] border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-3 text-[12px] text-amber-800 dark:text-amber-300">
                {isConflictLike ? (
                  <div className="font-semibold">
                    {currentStatus?.mapping_status === "not_contact_level_mappable"
                      ? "Dieser Bexio-Kontakt scheint mehrere Mietverhältnisse abzudecken. Das kann in B1c-v1 nicht sicher als 1:1 Kontakt-Zuordnung gespeichert werden."
                      : "Mehrere mögliche Mietverhältnisse. Keine Vorauswahl — bitte bewusst prüfen. Falls mehrere gleichzeitig korrekt sind, bitte nicht zuordnen und für B1c.2 offen lassen."}
                  </div>
                ) : null}
                {warnings.map((w, i) => (
                  <div key={i} className="mt-1">
                    {w}
                  </div>
                ))}
              </div>
            ) : null}

            {isInvalid ? (
              <div className="rounded-[8px] border border-border bg-card p-4 text-[12px] text-muted-foreground">
                Bitte zuerst die ungültige Zuordnung entfernen. Danach kann der
                Kontakt neu zugeordnet werden.
              </div>
            ) : loadingSug ? (
              <div className="text-[12px] text-muted-foreground">
                Vorschläge werden geladen …
              </div>
            ) : suggestions.length === 0 ? (
              <div className="rounded-[8px] border border-border bg-card p-4 text-[12px] text-muted-foreground">
                Keine Vorschläge. Möglicherweise kein Mieter-Kontakt oder noch nicht
                in Vantio erfasst.
              </div>
            ) : (
              <div className="space-y-2">
                {suggestions.map((s) => (
                  <label
                    key={s.matched_entity_id}
                    className={`block cursor-pointer rounded-[8px] border p-3 ${
                      selectedId === s.matched_entity_id
                        ? "border-primary bg-primary/10"
                        : "border-border bg-card"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <input
                        type="radio"
                        name="tenancy-candidate"
                        className="mt-1"
                        checked={selectedId === s.matched_entity_id}
                        onChange={() => setSelectedId(s.matched_entity_id)}
                      />
                      <div className="min-w-0">
                        <div className="text-[13px] font-semibold text-foreground">
                          {s.tenancy_reference || s.matched_entity_id}
                          <span className="ml-2">
                            <Badge tone={confidenceTone(s.confidence)}>
                              {CONFIDENCE_LABEL[s.confidence] || s.confidence}
                            </Badge>
                          </span>
                        </div>
                        <div className="mt-1 text-[11px] text-foreground">
                          Hauptmieter: {s.primary_tenant_name || "—"}
                          {s.co_tenant_names?.length
                            ? ` · Co-Mieter: ${s.co_tenant_names.join(", ")}`
                            : ""}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          {s.room_name || "—"}
                          {s.unit_label ? ` · ${s.unit_label}` : ""}
                        </div>
                        {s.reasons?.length ? (
                          <div className="mt-1 text-[10px] text-muted-foreground">
                            {s.reasons.join(" · ")}
                          </div>
                        ) : null}
                        {s.blocking_warnings?.length ? (
                          <div className="mt-1 text-[10px] text-amber-800 dark:text-amber-300">
                            {s.blocking_warnings.join(" · ")}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-[8px] border border-input px-3 py-1.5 text-[12px] text-foreground hover:bg-muted"
              >
                Abbrechen
              </button>
              {!isInvalid ? (
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={!selectedId || busy}
                  className="rounded-[8px] bg-primary px-4 py-1.5 text-[12px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isMapped ? "Zuordnung ändern" : "Zuordnung bestätigen"}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
